import { useTranslation } from 'react-i18next'

import {
  SegmentedControl,
  type SegmentedOption,
} from '@/components/ui/SegmentedControl'

/* ─────────────────────────────────────────────────────────────────────────────
 *  THE ONE DATE-RANGE VOCABULARY
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * Every finance surface and the dashboard filter by the SAME four values, and
 * they are declared here once because two declarations is how they drifted in
 * the first place: `FinancePage.tsx` filtered by `1M/3M/1Y/all` while
 * `useDashboard.ts` exported `1W/1M/3M/1Y` for tiles that were not built yet.
 * A founder who narrows the dashboard to a week and the receivables list to a
 * month is comparing two different questions and cannot tell — which is the
 * failure, not the duplication itself (Story 6.3, plan step 7).
 *
 * `1W` is DROPPED. A finance period is a month, a quarter, a year or the whole
 * book; a week of receivables is noise around a single invoice. `all` is what
 * the dashboard's set lacked and what a balance needs — "everything still
 * owed" has no start date.
 *
 * `useDashboard.ts` re-exports `DateRange` and `DATE_RANGES` from here rather
 * than declaring its own, so the dashboard cannot acquire a fifth value
 * without this file gaining it too.
 *
 * The control itself is a thin composition over SegmentedControl and is
 * deliberately thin: the roving tabindex, the sliding thumb and the "a
 * segmented control has no empty state" rule all live there, and a second
 * hand-rolled group of buttons would carry none of them.
 */

/** The canonical filter period. FR-53's shared control filters by exactly
 *  these, on every surface that has a date range at all. */
export type DateRange = '1M' | '3M' | '1Y' | 'all'

/* 1M/3M/1Y are notation and read identically in every language; only "All" is
 * a word. Both still go through the catalogue by KEY rather than as literals —
 * the Russian catalogue documents beside its own entries why the chips stay
 * Latin, and a literal here would put that decision in two places and make one
 * of them unreviewable (UX-DR-30). */
export const DATE_RANGES = [
  { value: '1M', labelKey: 'common.dateRange.1M' },
  { value: '3M', labelKey: 'common.dateRange.3M' },
  { value: '1Y', labelKey: 'common.dateRange.1Y' },
  { value: 'all', labelKey: 'common.dateRange.all' },
] as const satisfies readonly SegmentedOption<DateRange>[]

/** Narrows an untrusted string — a URL parameter, a stored preference — to a
 *  range this build knows.
 *
 *  A filtered view is linkable, so `?range=` arrives from outside and may say
 *  anything, including `1W` from a bookmark taken before the vocabulary was
 *  reconciled. A caller that trusted it would ask the server for a period the
 *  server does not implement and render whatever came back as though it were
 *  the period on the chip. */
/* eslint-disable-next-line react-refresh/only-export-components -- the guard
   lives with the list it guards. Splitting the vocabulary across two modules so
   that hot reload can keep component state on this one file is how a second
   definition gets started, and this file exists because that already happened
   once. */
export function isDateRange(value: string | null | undefined): value is DateRange {
  return DATE_RANGES.some((option) => option.value === value)
}

export interface DateRangeControlProps {
  value: DateRange
  onChange: (value: DateRange) => void
  /** Defaults to all four. A surface with a narrower set passes its own —
   *  never a wider one, since a value not in `DateRange` has no server. */
  options?: readonly SegmentedOption<DateRange>[]
  /** The accessible name. Defaults to the plain "Date range"; a screen with
   *  more than one range control must name each ("Turnover date range"), or
   *  assistive tech announces two identical groups. */
  label?: string
  size?: 'sm' | 'md'
  className?: string
}

/** The shared date-range filter (FR-53, Story 6.3 AC 3).
 *
 *  Every finance view and every dashboard tile that filters by period mounts
 *  THIS, rather than each building its own segmented control — that
 *  reinvention is what AC 3 rules out, and it is what would make a change to
 *  the vocabulary a sweep through five files. */
export function DateRangeControl({
  value,
  onChange,
  options = DATE_RANGES,
  label,
  size = 'sm',
  className,
}: DateRangeControlProps) {
  const { t } = useTranslation()

  return (
    <SegmentedControl<DateRange>
      options={options}
      value={value}
      onChange={onChange}
      label={label ?? t('common.dateRange.shared')}
      size={size}
      className={className}
    />
  )
}
